// 3rd party
import React, { ReactElement, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Types
import { BillAddress, ChangeEvent, ModalSize, Plant, PlantAddress } from 'types';
import { CartOrderDetails } from 'types/parts/cart';

// Styling
const Root = styled.div`
  padding: 0.75rem 1.5rem 1.25rem 2.375rem;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 6.25rem;
  overflow: auto;
  margin-bottom: -20.5rem;
`;

// Components
import {
  Checkbox,
  Modal,
  Input,
  Typography,
  Button,
  InformationTooltip,
  CustomSelect,
  TooltipWrapper
} from 'components';
import { useGetPlantsQuery } from 'api';
import PlantAutofillTable from './PlantAutofillTable';

// Hook
import { useUser } from 'selectors';
import theme from 'themes';
import { IcoUploadFile } from 'icons/indexed-icons/IcoUploadFile';
import ChipContainer from './Chip';
import { isAlphaNumeric } from 'common/helpers/v1';
import { ToastMsg } from 'common/components/Toast';

// Constants
import breakpoint from 'constants/breakpoints';
import { generateAnalyticsEvent } from 'common/helpers/v1/analytics';
import { AnalyticsCategories, ECommerceAnalyticEventActions } from 'constants/analytics';
import DatePicker from 'components/DatePicker';
import InformationCircle from 'icons/indexed-icons/InformationCircle';
import { format } from 'date-fns';
import Asterisk from 'pages/AlertsPage/Step3/Asterisk ';
import StyledTextArea from 'components/TextArea';
import { ContentAlign } from 'common/components/common-styled';
import styledTheme from 'common/theme';
import { convertPlantAddressToString } from 'components/CartWizard/helper';

const Container = styled.div`
  width: 50%;
  min-width: 13.5rem;
  padding-right: 1.875rem;

  &:nth-child(2n) {
    padding-right: 0;
    padding-left: 1.875rem;
    @media (max-width: ${breakpoint.size.md}px) {
      padding-left: 0;
    }
  }

  @media (max-width: ${breakpoint.size.md}px) {
    width: 100%;
    padding-right: 0;
  }
`;

export const CustomText = styled.div`
  width: 15rem;
  color: #303132;
  text-wrap: break-word;
  text-align: center;
  font-family: var(--Font-Family-Body, Roboto);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
`;
const InputRowBase = styled.div`
  height: 2.5rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const InputRow = styled(InputRowBase)`
  & > div {
    flex-grow: 1;
    margin-right: 0.8125rem;
    display: flex;
    align-items: center;
  }

  & > div:last-child {
    margin-right: 0;
  }
`;

const InputRowAddressButtons = styled(InputRowBase)`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`;

interface InputContainerProps {
  justify?: 'flex-start' | 'center' | 'flex-end';
}
const InputContainer = styled.div<InputContainerProps>`
  display: flex;
  justify-content: ${({ justify }) => (justify ? justify : 'flex-start')};

  input {
    border-radius: 0.375rem;
  }
  input:focus {
    outline: none;
  }
  input[type='checkbox'] {
    border-radius: 0.375rem;
  }
  .purchase_order-input {
    margin-right: 20px;
  }
  .purchase_order-input,
  .purchange_order-label {
    flex: 1;
  }
  .purchase_order-input {
    max-width: 50%;
  }
  .creator{
    width: 50%;
  }
  .name-input, .white{
    border: 1px solid ${styledTheme.JBTColors.mainColor} !important;
  }
    .
`;

const CalendarContainer = styled.div`
  width: 50%;
`;
const DateField = styled.div`
  .date-picker{
    flex 1;
  }
  display: flex;
  align-items: center;
  gap: 1rem;
  .tooltip-text{
    white-space: nowrap;
    word-break: keep-all;
    font-weight: 400;
  }
    .icon--information-circle{
      width: 1.25rem;
      height: 1.25rem;
    }
`;
const BillToRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const AddressButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  @media (max-width: ${breakpoint.size.sm}px) {
    flex-direction: column;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLable = styled.label`
  width: 15rem;
  display: flex;
  align-items: center;
  color: ${theme.colors.primaryBlue5};
  font-weight: 500;
  gap: 0.5rem;
  line-height: normal;
  cursor: pointer;
  flex-wrap: wrap;
  align-items: center;
`;

// Props
interface Props {
  orderDetails: CartOrderDetails;
  handleUpdate: (updatedDetails: CartOrderDetails) => void;
  isUrgent?: boolean;
}
type BillingFieldState = 'white' | 'disabled';

interface EmptyFields {
  streetAddress: BillingFieldState;
  suiteNumber: BillingFieldState;
  city: BillingFieldState;
  state: BillingFieldState;
  postalCode: BillingFieldState;
  country: BillingFieldState;
}
interface EmptyFieldsState extends EmptyFields {
  shippingAddrState?: BillingFieldState;
}
const getAddressObject = (addr?: PlantAddress): BillAddress => {
  return {
    streetAddress: addr?.addressLine1 || '',
    suiteNumber: addr?.addressLine2 || '',
    city: addr?.city || '',
    state: addr?.state || '',
    postalCode: addr?.zipCode || '',
    country: addr?.country || ''
  };
};
const CartFulfillmentDetails = ({ orderDetails, handleUpdate, isUrgent }: Props): ReactElement => {
  const [showAddressModal, setShowAddressModal] = useState<boolean>(false);
  const [emptyFields, setEmptyFields] = useState<EmptyFieldsState>();
  const [shipAddress, setShipAddress] = useState<PlantAddress[]>([]);
  const { data: plants, isFetching: plantsLoading } = useGetPlantsQuery();
  const user = useUser();
  const { t } = useTranslation(['fpns']);

  const [fileList, setFileList] = useState<File[] | undefined>();

  // to get all the bill to and ship to data from /plants api.
  const addressData: {
    billAddress: PlantAddress[];
    shipAddress: PlantAddress[];
  } = useMemo(() => {
    const result = {
      billAddress: [] as PlantAddress[],
      shipAddress: [] as PlantAddress[]
    };

    plants?.forEach((item) => {
      if (item?.useBillAddr && item?.billAddress && item?.billAddress?.length > 0) {
        const addresses = item.billAddress.map((addr) => ({
          ...addr,
          name: item.name,
          accountId: item.id
        }));
        result.billAddress.push(...addresses);
      } else {
        const addresses = item?.shipAddress?.map((addr) => ({
          ...addr,
          name: item.name,
          accountId: item.id
        }));
        if (addresses) result.billAddress.push(...addresses);
      }
    });

    return result || {};
  }, [plants]);

  // If the user data flows through after initialization
  useEffect(() => {
    if (user && orderDetails.creator === '') {
      handleUpdate({ ...orderDetails, creator: user.email });
    }
  }, [user, orderDetails]);

  // auto populate the input field if there is only one bill/ship to address.
  useEffect(() => {
    const { billAddress = [] } = addressData;
    const plant = plants?.[0];
    if (!plant) return;
    if (billAddress.length === 1) {
      autofillAddress(billAddress[0]);
    }
  }, [addressData, plants]);

  // Trigger analytic event when component mounts
  useEffect(() => {
    generateAnalyticsEvent({
      category: AnalyticsCategories.ECOMMERCE,
      action: ECommerceAnalyticEventActions.ADD_SHIPPING_INFO,
      event: ECommerceAnalyticEventActions.ADD_SHIPPING_INFO
    });
  }, []);

  // convert the shipAddress into required format for the dropdown
  const shipOptions = shipAddress.map((ship) => {
    return {
      value: ship,
      label: ship.accountName,
      secondarylabel: convertPlantAddressToString(ship)
    };
  });

  // Input change handling functions
  const handleDetailsUpdate = (details: CartOrderDetails) => {
    handleUpdate(details);
  };

  const handleDateChange = (date: Date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    handleUpdate({ ...orderDetails, requestedDate: formattedDate });
  };

  const handleShippingAddress = (address: PlantAddress) => {
    const shipAddress = {
      streetAddress: address.addressLine1,
      suiteNumber: address.addressLine2 ? address.addressLine2 : '',
      city: address.city,
      state: address.state,
      postalCode: address.zipCode,
      country: address.country
    };
    handleDetailsUpdate({
      ...orderDetails,
      shippingAddress: shipAddress
    });
  };
  const handleNameChange = (name: string, propName: 'firstName' | 'lastName') => {
    const copyBillee = { ...orderDetails.billee, [propName]: name.trimStart() };
    const copyDetails = { ...orderDetails, billee: { ...copyBillee } };
    handleDetailsUpdate(copyDetails);
  };

  const autofillAddress = (plant: PlantAddress | undefined): void => {
    if (plant) {
      const billingAddress = getAddressObject(plant);
      const selectedPlantData = plants?.find((item) => item.id === plant?.accountId);
      const shipAddress = selectedPlantData?.shipAddress?.map((addr) => ({
        ...addr,
        accountName: selectedPlantData.name
      }));
      const shipToAddress = shipAddress?.length === 1 ? getAddressObject(shipAddress?.[0]) : null;
      handleDetailsUpdate({
        ...orderDetails,
        billingAddress: billingAddress,
        accountName: plant.name,
        customerNumber: plant.custNum,
        ...(shipToAddress && { shippingAddress: shipToAddress }),
        accountId: plant.accountId
      });
      // Initialize the field states with type safety
      const addressFieldStates: EmptyFields = {} as EmptyFields;

      for (const key in billingAddress) {
        const fieldKey = key as keyof BillAddress; // Type-safe key
        addressFieldStates[fieldKey] = billingAddress[fieldKey] ? 'disabled' : 'white';
      }
      if (!shipAddress?.length) {
        setEmptyFields({ ...addressFieldStates, shippingAddrState: 'white' });
      } else {
        setEmptyFields(addressFieldStates);
      }
      setShipAddress(shipAddress || []);
    } else {
      // clear address field
      handleDetailsUpdate({
        ...orderDetails,
        billingAddress: getAddressObject() || {},
        shippingAddress: getAddressObject() || {},
        shipMatchBilling: false,
        accountName: ''
      });
      setEmptyFields({} as EmptyFields);
    }
    if (plant) {
      ToastMsg({
        heading: 'Success',
        type: 'success',
        message: t('autofilled_address', { item: plant.name })
      });
    } else {
      ToastMsg({ heading: 'Success', type: 'success', message: t('cleared_address') });
    }
  };

  const handleAddressChange = (
    val: string,
    billOrShip: 'billingAddress' | 'shippingAddress',
    propName:
      | 'streetAddress'
      | 'suiteNumber'
      | 'city'
      | 'state'
      | 'postalCode'
      | 'country'
      | 'notes'
  ) => {
    const copyAddr = { ...orderDetails[billOrShip], [propName]: val.trimStart() };
    const copyDetails = { ...orderDetails, [billOrShip]: { ...copyAddr } };
    handleDetailsUpdate(copyDetails);
  };

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const handleFileUpload = (event: any) => {
    const allFiles: File[] = fileList
      ? ([...fileList, ...event.target?.files] as File[])
      : [...event.target?.files];
    event.target.value = ''; // Reset the input value to allow re-uploading the same file
    const files = allFiles.filter((file) => {
      if (file.size > 10000000) {
        ToastMsg({
          heading: 'Error',
          message: 'File should be less than 10MB',
          type: 'error'
        });
      } else if (!file.name.endsWith('.pdf')) {
        ToastMsg({
          heading: 'Error',
          message: 'File should be a PDF',
          type: 'error'
        });
      } else {
        return file;
      }
    });
    setFileList(files);
    handleDetailsUpdate({ ...orderDetails, files: files });
  };

  const handleFileRemove = (file?: File) => {
    const remainingFiles = fileList?.filter((f) => f.name !== file?.name);
    setFileList(remainingFiles);
    handleDetailsUpdate({ ...orderDetails, files: remainingFiles });
  };

  return (
    <Root>
      <Container>
        <BillToRow>
          <Typography mb={0} variant="subtitle">
            {t('bill_to')}
            <Asterisk />
          </Typography>
        </BillToRow>
        {orderDetails.accountName && (
          <InputRow>
            <InputContainer>
              <Input
                placeholder={t('first_name', { ns: 'common' })}
                width="50%"
                value={orderDetails?.accountName}
                variant={'disabled'}
                borderVariant="none"
              />
            </InputContainer>
          </InputRow>
        )}
        <InputRow>
          <InputContainer>
            <Input
              variant="white"
              placeholder={t('first_name', { ns: 'common' })}
              width="50%"
              value={orderDetails?.billee?.firstName}
              onChange={(event: ChangeEvent) => {
                const firstName = event.target.value as string;
                handleNameChange(firstName, 'firstName');
              }}
              className="name-input"
            />
          </InputContainer>
          <InputContainer>
            <Input
              variant="white"
              placeholder={t('last_name', { ns: 'common' })}
              value={orderDetails?.billee?.lastName}
              onChange={(event: ChangeEvent) => {
                const lastName = event.target.value as string;
                handleNameChange(lastName, 'lastName');
              }}
              className="name-input"
            />
          </InputContainer>
        </InputRow>
        <InputRow>
          <InputContainer>
            <Input
              variant={emptyFields?.streetAddress || 'disabled'}
              placeholder={t('street_address', { ns: 'common' })}
              value={orderDetails?.billingAddress?.streetAddress ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'billingAddress', 'streetAddress');
              }}
              className={emptyFields?.streetAddress}
            />
          </InputContainer>
        </InputRow>
        <InputRow>
          <InputContainer>
            <Input
              variant={emptyFields?.suiteNumber || 'disabled'}
              placeholder={t('app_suite_unit', { ns: 'common' })}
              value={orderDetails?.billingAddress?.suiteNumber ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'billingAddress', 'suiteNumber');
              }}
              className={emptyFields?.suiteNumber}
            />
          </InputContainer>
        </InputRow>
        <InputRow>
          <InputContainer>
            <Input
              variant={emptyFields?.city || 'disabled'}
              placeholder={t('city', { ns: 'common' })}
              value={orderDetails?.billingAddress?.city ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'billingAddress', 'city');
              }}
              className={emptyFields?.city}
            />
          </InputContainer>
          <InputContainer>
            <Input
              variant={emptyFields?.state || 'disabled'}
              placeholder={t('state', { ns: 'common' })}
              value={orderDetails?.billingAddress?.state ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'billingAddress', 'state');
              }}
              className={emptyFields?.state}
            />
          </InputContainer>
        </InputRow>
        <InputRow>
          <InputContainer>
            <Input
              variant={emptyFields?.postalCode || 'disabled'}
              placeholder={t('postal_code', { ns: 'common' })}
              value={orderDetails?.billingAddress?.postalCode ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'billingAddress', 'postalCode');
              }}
              className={emptyFields?.postalCode}
            />
          </InputContainer>
          <InputContainer>
            <Input
              variant={emptyFields?.country || 'disabled'}
              placeholder={t('country', { ns: 'common' })}
              value={orderDetails?.billingAddress?.country ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'billingAddress', 'country');
              }}
              className={emptyFields?.country}
            />
          </InputContainer>
        </InputRow>
        <InputRowAddressButtons>
          <AddressButtons>
            <Button variant="outline-blue" size="small" onClick={() => autofillAddress(undefined)}>
              {t('clear_address')}
            </Button>
            <Button
              variant="filled-primary"
              size="small"
              disabled={!plants || plantsLoading}
              onClick={() => setShowAddressModal(!showAddressModal)}
            >
              {t('use_saved_address')}
            </Button>
          </AddressButtons>
        </InputRowAddressButtons>
      </Container>
      <Container>
        <Typography mb={0} variant="subtitle">
          {t('ship_to')}
          <Asterisk />
        </Typography>
        {orderDetails.accountName && (
          <InputRow>
            <InputContainer>
              <Input
                placeholder={t('first_name', { ns: 'common' })}
                width="50%"
                value={orderDetails?.accountName}
                variant={'disabled'}
                borderVariant="none"
              />
            </InputContainer>
          </InputRow>
        )}
        {shipAddress.length > 1 && (
          <InputRow>
            <InputContainer>
              <CustomSelect
                id="ship-address"
                mandatory
                options={shipOptions}
                // value={machineType ? machineType : ''}
                onChange={(selected) => {
                  handleShippingAddress(selected.value);
                }}
                placeholder="Select from saved address"
              />
            </InputContainer>
          </InputRow>
        )}
        <InputRow>
          <InputContainer>
            <Input
              variant={emptyFields?.shippingAddrState || 'disabled'}
              placeholder={t('street_address', { ns: 'common' })}
              value={orderDetails?.shippingAddress?.streetAddress ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'shippingAddress', 'streetAddress');
              }}
            />
          </InputContainer>
        </InputRow>
        <InputRow>
          <InputContainer>
            <Input
              variant={emptyFields?.shippingAddrState || 'disabled'}
              placeholder={t('app_suite_unit', { ns: 'common' })}
              value={orderDetails?.shippingAddress?.suiteNumber ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'shippingAddress', 'suiteNumber');
              }}
            />
          </InputContainer>
        </InputRow>
        <InputRow>
          <InputContainer>
            <Input
              variant={emptyFields?.shippingAddrState || 'disabled'}
              placeholder={t('city', { ns: 'common' })}
              value={orderDetails?.shippingAddress?.city ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'shippingAddress', 'city');
              }}
            />
          </InputContainer>
          <InputContainer>
            <Input
              variant={emptyFields?.shippingAddrState || 'disabled'}
              placeholder={t('state', { ns: 'common' })}
              value={orderDetails?.shippingAddress?.state ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'shippingAddress', 'state');
              }}
            />
          </InputContainer>
        </InputRow>

        <InputRow>
          <InputContainer>
            <Input
              variant={emptyFields?.shippingAddrState || 'disabled'}
              placeholder={t('postal_code', { ns: 'common' })}
              value={orderDetails?.shippingAddress?.postalCode ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'shippingAddress', 'postalCode');
              }}
            />
          </InputContainer>
          <InputContainer>
            <Input
              variant={emptyFields?.shippingAddrState || 'disabled'}
              placeholder={t('country', { ns: 'common' })}
              value={orderDetails?.shippingAddress?.country ?? ''}
              onChange={(event: ChangeEvent) => {
                const val = event.target.value as string;
                handleAddressChange(val, 'shippingAddress', 'country');
              }}
            />
          </InputContainer>
        </InputRow>

        <StyledTextArea
          padding=".2rem .5rem"
          rows={5}
          value={orderDetails.notes}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            const val = event.target.value as string;
            handleUpdate({ ...orderDetails, notes: val });
          }}
          maxLength={250}
          placeholder="250 character max."
        />
      </Container>
      <Container>
        <ContentAlign alignItems="center" gap=".3rem">
          <Typography mb={0} color={theme.colors.black} variant="subtitle">
            {t('shipment_method')}
          </Typography>
          <TooltipWrapper
            Tooltip={<CustomText>{t('shipment_method_message')}</CustomText>}
            textColor={theme.colors.textLightBlack}
            backGroundColor={theme.colors.white}
          >
            <InformationCircle />
          </TooltipWrapper>
        </ContentAlign>
        <InputContainer>
          <Checkbox
            width={20}
            height={20}
            id={'allow_partial_shipments'}
            label={t('allow_partial_shipments') as string}
            checked={isUrgent}
            margin="0.5rem 0 2rem"
            disabled
          />
        </InputContainer>
        <Typography mb={0} color={theme.colors.black} variant="subtitle">
          {t('created_by')}
        </Typography>
        <InputContainer>
          <Input
            variant="disabled"
            borderVariant="none"
            placeholder="creator@comp.com"
            value={orderDetails?.creator}
            onChange={(event: ChangeEvent) => {
              const creator = event.target.value as string;
              handleDetailsUpdate({ ...orderDetails, creator: creator.trimStart() });
            }}
            className="creator"
          />
        </InputContainer>
      </Container>
      <Container>
        <Typography mb={0} color={theme.colors.black} variant="subtitle">
          {t('purchase_order_info')}
          {orderDetails.purchaseOrderRequired && <Asterisk />}
        </Typography>
        <InputContainer>
          <Input
            className="purchase_order-input"
            variant="white"
            placeholder={t('add_po_number')}
            value={orderDetails?.customerPoNumber}
            onChange={(event: ChangeEvent) => {
              const customerPo = event.target.value as string;
              if (event.target.value.length <= 20 && (customerPo === '' || isAlphaNumeric(customerPo))) {
                handleDetailsUpdate({ ...orderDetails, customerPoNumber: customerPo.trimStart() });
              }
            }}
          />
          <FileInputLable className="purchange_order-label" htmlFor="order_pdf">
            <IcoUploadFile />
            Upload PDF{' '}
            <Typography mb={0} color={theme.colors.text.lightBlack}>
              <i>(Size limit 10mb)</i>
            </Typography>
          </FileInputLable>
          <FileInput
            accept="application/pdf"
            multiple
            onChange={handleFileUpload}
            type="file"
            id="order_pdf"
          />
        </InputContainer>
        <ChipContainer margin="1rem 0" files={fileList} handleRemove={handleFileRemove} />
        <DateField>
          <CalendarContainer>
            <DatePicker handleDateChange={handleDateChange} date={orderDetails.requestedDate} />
          </CalendarContainer>
          <TooltipWrapper
            Tooltip={<CustomText>{'Requested delivery date to deliver the parts'}</CustomText>}
            textColor={theme.colors.textLightBlack}
            backGroundColor={theme.colors.white}
          >
            <InformationCircle />
          </TooltipWrapper>
        </DateField>
      </Container>
      <Modal
        visible={showAddressModal}
        size={ModalSize.SMALL}
        title={t('saved_addresses') as string}
        onClose={() => setShowAddressModal(false)}
        allowContentScroll={true}
      >
        <PlantAutofillTable
          plants={addressData.billAddress}
          autoFillFunction={autofillAddress}
          showModalFunction={setShowAddressModal}
        />
      </Modal>
    </Root>
  );
};

export default CartFulfillmentDetails;
